import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VehiclePicker } from "@/components/VehiclePicker";
import { Upload, X, Loader2, Package, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: "", category: "wheels", bolt_pattern: "", diameter: "", width: "", offset: "",
  tire_size: "", brand: "", model: "", condition: "New", quantity: 4, price: "",
  location: "", zip_code: "", fulfillment: "pickup", photos: [], proof_photo: null, description: "",
};

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">{label}{required && <span className="text-blaze"> *</span>}</Label>
    {children}
  </div>
);

const Sel = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-[#0A0A0A] border-border rounded-sm h-10"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#141414] border-border max-h-72">
      {options.map((o) => { const v = typeof o === "object" ? o.value : o; const l = typeof o === "object" ? o.label : o; return <SelectItem key={v} value={v} className="spec-block">{l}</SelectItem>; })}
    </SelectContent>
  </Select>
);

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [constants, setConstants] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const proofRef = useRef();

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/constants").then((r) => setConstants(r.data));
    if (editing) api.get(`/listings/${id}`).then((r) => setForm({ ...empty, ...r.data }));
  }, [id, editing]);

  const showWheelSpecs = form.category === "wheels" || form.category === "set";
  const showTireSpecs = form.category === "tires" || form.category === "set";

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, r.data.url] }));
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const onProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, proof_photo: r.data.url }));
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingProof(false);
      if (proofRef.current) proofRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.title || !form.brand || !form.price || !form.location) { toast.error("Fill all required fields"); return; }
    if (form.photos.length === 0) { toast.error("Add at least one photo — real photos build trust"); return; }
    if (showWheelSpecs && !form.bolt_pattern) { toast.error("Bolt pattern is required for wheels"); return; }
    if (showTireSpecs && !form.tire_size) { toast.error("Tire size is required for tires"); return; }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), quantity: parseInt(form.quantity) || 1 };
    if (!showWheelSpecs) { payload.bolt_pattern = ""; payload.diameter = ""; payload.width = ""; payload.offset = ""; }
    if (!showTireSpecs) { payload.tire_size = ""; }
    try {
      const r = editing ? await api.put(`/listings/${id}`, payload) : await api.post("/listings", payload);
      toast.success(editing ? "Listing updated" : "Listing published");
      navigate(`/listing/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!constants) return <div className="max-w-3xl mx-auto px-6 py-16"><Loader2 className="w-6 h-6 animate-spin text-blaze mx-auto" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading font-900 text-3xl uppercase tracking-tight mb-1">{editing ? "Edit Listing" : "Create Listing"}</h1>
      <p className="text-sm text-muted-foreground mb-8">Structured fields help buyers find exactly what fits their build.</p>

      <div className="space-y-6">
        {/* Photos */}
        <Field label="Photos" required>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {form.photos.map((p, i) => (
              <div key={i} className="relative aspect-square bg-[#1C1C1C] border border-border rounded-sm overflow-hidden group">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="aspect-square border border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-blaze hover:text-blaze transition-colors">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-[10px] uppercase tracking-wider">Add</span>
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="photo-input" />
        </Field>

        <div className="bg-[#141414] border border-blaze/30 rounded-sm p-4">
          <Field label="Fitment Proof Photo">
            <p className="text-[11px] text-muted-foreground mb-3 -mt-1">Snap the bolt-pattern / offset stamp on the wheel (e.g. "6x139.7 ET18"). Listings with a proof photo get a <span className="text-blaze font-bold">Fitment Verified</span> badge that buyers trust.</p>
            <div className="flex items-center gap-3">
              {form.proof_photo ? (
                <div className="relative w-24 h-24 bg-[#1C1C1C] border border-blaze/40 rounded-sm overflow-hidden group">
                  <img src={fileUrl(form.proof_photo)} alt="proof" className="w-full h-full object-cover" />
                  <button onClick={() => setForm((f) => ({ ...f, proof_photo: null }))} className="absolute top-1 right-1 bg-black/70 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button data-testid="upload-proof-btn" onClick={() => proofRef.current?.click()} disabled={uploadingProof} className="w-24 h-24 border border-dashed border-blaze/40 rounded-sm flex flex-col items-center justify-center gap-1 text-blaze hover:bg-blaze/5 transition-colors">
                  {uploadingProof ? <Loader2 className="w-5 h-5 animate-spin" /> : <BadgeCheck className="w-5 h-5" />}
                  <span className="text-[10px] uppercase tracking-wider">Add proof</span>
                </button>
              )}
              {form.proof_photo && <span className="text-xs text-blaze font-bold uppercase tracking-wider flex items-center gap-1"><BadgeCheck className="w-4 h-4" /> Fitment Verified</span>}
            </div>
            <input ref={proofRef} type="file" accept="image/*" onChange={onProof} className="hidden" data-testid="proof-input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Category" required><Sel value={form.category} onChange={set("category")} options={constants.categories} testid="form-category" /></Field>
          <Field label="Condition" required><Sel value={form.condition} onChange={set("condition")} options={constants.conditions} testid="form-condition" /></Field>
        </div>

        <Field label="Title" required>
          <Input data-testid="form-title" value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder='e.g. 20" Fuel Maverick w/ 35" Nittos' className="bg-[#0A0A0A] border-border rounded-sm h-10" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Brand" required><Input data-testid="form-brand" value={form.brand} onChange={(e) => set("brand")(e.target.value)} placeholder="Fuel, Method, BFGoodrich…" className="bg-[#0A0A0A] border-border rounded-sm h-10" /></Field>
          <Field label="Model"><Input data-testid="form-model" value={form.model} onChange={(e) => set("model")(e.target.value)} placeholder="Maverick D610" className="bg-[#0A0A0A] border-border rounded-sm h-10" /></Field>
        </div>

        {showWheelSpecs && (
          <div className="bg-[#141414] border border-border rounded-sm p-4 space-y-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-blaze">Wheel Fitment</div>
            <div className="border border-dashed border-border rounded-sm p-3">
              <VehiclePicker onResolve={(v) => setForm((f) => ({ ...f, bolt_pattern: v.bolt_pattern, diameter: f.diameter || (v.diameters?.[0] || "") }))} />
              <p className="text-[11px] text-muted-foreground mt-2">Pick a truck to auto-fill the bolt pattern, or set specs manually below.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Bolt Pattern" required><Sel value={form.bolt_pattern} onChange={set("bolt_pattern")} options={constants.bolt_patterns} testid="form-bolt-pattern" placeholder="5x139.7" /></Field>
              <Field label="Diameter"><Sel value={form.diameter} onChange={set("diameter")} options={constants.diameters} testid="form-diameter" placeholder="in" /></Field>
              <Field label="Width"><Sel value={form.width} onChange={set("width")} options={constants.widths} testid="form-width" placeholder="in" /></Field>
              <Field label="Offset"><Sel value={form.offset} onChange={set("offset")} options={constants.offsets} testid="form-offset" placeholder="ET" /></Field>
            </div>
          </div>
        )}

        {showTireSpecs && (
          <div className="bg-[#141414] border border-border rounded-sm p-4 space-y-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-blaze">Tire Fitment</div>
            <Field label="Tire Size" required><Sel value={form.tire_size} onChange={set("tire_size")} options={constants.tire_sizes} testid="form-tire-size" placeholder="35x12.50R20" /></Field>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Price ($)" required><Input data-testid="form-price" type="number" value={form.price} onChange={(e) => set("price")(e.target.value)} placeholder="1800" className="bg-[#0A0A0A] border-border rounded-sm h-10 spec-block" /></Field>
          <Field label="Quantity"><Input data-testid="form-quantity" type="number" value={form.quantity} onChange={(e) => set("quantity")(e.target.value)} className="bg-[#0A0A0A] border-border rounded-sm h-10 spec-block" /></Field>
          <Field label="Fulfillment" required><Sel value={form.fulfillment} onChange={set("fulfillment")} options={constants.fulfillment} testid="form-fulfillment" /></Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Field label="Location" required><Input data-testid="form-location" value={form.location} onChange={(e) => set("location")(e.target.value)} placeholder="Dallas, TX" className="bg-[#0A0A0A] border-border rounded-sm h-10" /></Field>
          </div>
          <Field label="ZIP Code"><Input data-testid="form-zip" value={form.zip_code} onChange={(e) => set("zip_code")(e.target.value)} placeholder="75201" className="bg-[#0A0A0A] border-border rounded-sm h-10 spec-block" /></Field>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-3">ZIP powers "near me" distance sorting so local buyers find you first.</p>

        <Field label="Description"><Textarea data-testid="form-description" value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="Condition notes, mileage on tires, any curb rash, mounting details…" className="bg-[#0A0A0A] border-border rounded-sm min-h-28" /></Field>

        <Button data-testid="submit-listing-btn" onClick={submit} disabled={saving} className="w-full bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold h-12 text-base gap-2">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Package className="w-5 h-5" />}
          {editing ? "Save Changes" : "Publish Listing"}
        </Button>
      </div>
    </div>
  );
}
